"use client";

import {
	Button,
	Image,
	Link,
	type DefaultLinkProps
} from "@pamperedchef/design-system";

import {
	PriceDisplay,
	ProductHighlightTag,
	ProductRating,
	selectHighlightTags
} from "@/composites/product";
import type { ListingProduct } from "@/domain/product-listing";

import * as styles from "./PlpProductCard.module.scss";

export interface PlpProductCardProps {
	product: ListingProduct;
	addToCartLabel?: string;
	onAddToCart?: (sku: string) => void;
	className?: string;
}

export function PlpProductCard({
	product,
	addToCartLabel = "Add to Cart",
	onAddToCart,
	className
}: PlpProductCardProps): JSX.Element {
	const { highlightTag } = product;

	const isInert = !onAddToCart;

	return (
		<article
			className={[styles["card"], className ?? ""]
				.filter(Boolean)
				.join(" ")}
			data-component="PlpProductCard"
			data-sku={product.sku}
			data-add-to-cart={isInert ? "inert" : "enabled"}
		>
			<a
				href={product.href}
				aria-hidden="true"
				tabIndex={-1}
				className={styles["card__image-link"]}
			>
				<Image
					src={product.image.src}
					alt={product.image.alt}
					aspectRatio="1:1"
					className={styles["card__image"]}
				/>
			</a>

			<div className={styles["card__body"]}>
				<ProductHighlightTag
					tags={selectHighlightTags(
						highlightTag ? [highlightTag] : []
					)}
				/>

				<Link<DefaultLinkProps>
					href={product.href}
					size="lg"
					className={styles["card__title"]}
				>
					{product.title}
				</Link>

				{product.rating ? (
					<ProductRating
						value={product.rating.value}
						count={product.rating.count}
					/>
				) : null}

				{product.price ? (
					<PriceDisplay
						display={product.price.display}
						strikeThrough={product.price.strikeThrough}
						currency={product.price.currency}
						locale={product.price.locale}
						size="small"
					/>
				) : null}

				<Button
					kind="secondary"
					size="sm"
					className={styles["card__cta"]}
					onPress={
						onAddToCart ? () => onAddToCart(product.sku) : undefined
					}
				>
					{addToCartLabel}
				</Button>
			</div>
		</article>
	);
}
